import { useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import { ArrowUp, ImagePlus, X } from "lucide-react";
import ReceiptAttachments, { appendReceiptFiles, receiptGroups, type ReceiptAttachment } from "../../components/ReceiptAttachments";

/** Moo's message box: text, attached receipt photos, and send. */
export default function MooComposer({
  text, setText, photos, setPhotos, busy, error, setError, composer, sending, onSend, onRetry, onDraftChange,
}: {
  text: string;
  setText: (text: string) => void;
  photos: ReceiptAttachment[];
  setPhotos: Dispatch<SetStateAction<ReceiptAttachment[]>>;
  /** What Moo is doing, or "" when idle. */
  busy: string;
  error: string;
  setError: (message: string) => void;
  composer: RefObject<HTMLTextAreaElement | null>;
  /** Whether a request is already on its way, which `busy` can lag behind by a render. */
  sending: () => boolean;
  onSend: () => void;
  onRetry?: () => void;
  /** Clears a previous composer error when the draft changes. */
  onDraftChange: () => void;
}) {
  const upload = useRef<HTMLInputElement>(null);
  const [photoPreview, setPhotoPreview] = useState("");
  useEffect(() => {
    if (!photos.length) {
      setPhotoPreview("");
      return;
    }
    const url = URL.createObjectURL(photos[0].file);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photos]);
  useEffect(() => { if (!text && composer.current) composer.current.style.height = "auto"; }, [text, composer]);
  const receiptCount = receiptGroups(photos).length;
  return (
    <footer className="moo-footer">
      {error && (
        <p className="form-error" role="alert">
          {error}
          {onRetry && <button className="text-button" onClick={onRetry}>Retry message</button>}
        </p>
      )}
      {!!photos.length && (
        <div className="moo-attachment moo-attachments">
          {photoPreview && (
            <img src={photoPreview} alt="Attached image preview" />
          )}
          <span>{photos.length} {photos.length === 1 ? "photo" : "photos"} · {receiptCount} {receiptCount === 1 ? "receipt" : "receipts"}</span>
          <button
            disabled={!!busy}
            aria-label="Remove attachment"
            onClick={() => {
              setPhotos([]);
              onDraftChange();
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}
      <div className="moo-composer">
        <textarea
          ref={composer}
          value={text}
          rows={2}
          maxLength={8000}
          disabled={!!busy}
          placeholder="Ask Moo…"
          aria-label="Message Moo"
          onPaste={(e) => {
            const images = Array.from(e.clipboardData.items).filter((item) => item.kind === "file" && item.type.startsWith("image/")).map((item) => item.getAsFile()).filter((file): file is File => !!file);
            if (!images.length) return;
            e.preventDefault();
            if (busy || sending()) return;
            if (images.some((image) => image.size > 20_000_000)) {
              setError("Choose an image under 20 MB.");
              return;
            }
            setPhotos((current) => appendReceiptFiles(current, images));
            setError("");
            onDraftChange();
          }}
          onChange={(e) => {
            setText(e.target.value);
            onDraftChange();
            e.target.style.height = "auto";
            e.target.style.height =
              Math.min(e.target.scrollHeight, 220) + "px";
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              onSend();
            }
          }}
        />
        <div>
          <label
            className={`moo-attach-button${busy ? " disabled" : ""}`}
            role="button"
            aria-label="Attach receipt photo"
            tabIndex={busy ? -1 : 0}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !busy) {
                e.preventDefault();
                upload.current?.click();
              }
            }}
          >
            <ImagePlus size={22} />
            <input
              ref={upload}
              className="sr-only"
              type="file"
              accept="image/*"
              multiple
              disabled={!!busy}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) {
                  if (files.some((image) => image.size > 20_000_000)) {
                    setError("Choose an image under 20 MB.");
                    e.target.value = "";
                    return;
                  }
                  setPhotos((current) => appendReceiptFiles(current, files));
                  setError("");
                  onDraftChange();
                }
                e.target.value = "";
              }}
            />
          </label>
          <small>Review first. Save when ready.</small>
          <button
            className="moo-send"
            disabled={!!busy || (!text.trim() && !photos.length)}
            aria-label="Send message"
            onClick={onSend}
          >
            <ArrowUp size={22} />
          </button>
        </div>
      </div>
      {photos.length > 1 && <ReceiptAttachments items={photos} onChange={setPhotos} disabled={!!busy} />}
      <p className="moo-privacy">
        Messages and relevant records go to an AI provider (OpenAI or Gemini). Chats stay with
        this profile. A saved receipt keeps its photo privately in Barnyard.
      </p>
    </footer>
  );
}
